import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { applyPalette } from './ThemePicker';

interface Command {
  id: string;
  label: string;
  category: string;
  action: () => void;
  keywords?: string;
}

function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const commands: Command[] = useMemo(
    () => [
      {
        id: 'nav-dashboard',
        label: 'Go to Dashboard',
        category: 'Navigation',
        action: () => navigate('/'),
        keywords: 'home overview kpi',
      },
      {
        id: 'nav-agents',
        label: 'Go to Agents',
        category: 'Navigation',
        action: () => navigate('/roles'),
        keywords: 'bots fleet agents roles',
      },
      {
        id: 'nav-conversations',
        label: 'Go to Conversations',
        category: 'Navigation',
        action: () => navigate('/conversations'),
        keywords: 'chats messages threads',
      },
      {
        id: 'nav-guardrails',
        label: 'Go to Bot Guardrails',
        category: 'Navigation',
        action: () => navigate('/settings'),
        keywords: 'rules banned phrases policies guardrails',
      },
      {
        id: 'nav-business-ops',
        label: 'Go to Business Ops',
        category: 'Navigation',
        action: () => navigate('/business-ops'),
        keywords: 'operations invoices suppliers reminders bank transfers',
      },
      {
        id: 'nav-insights',
        label: 'Go to Insights',
        category: 'Navigation',
        action: () => navigate('/insights'),
        keywords: 'charts metrics stats reports analytics',
      },
      {
        id: 'nav-settings',
        label: 'Go to Settings',
        category: 'Navigation',
        action: () => navigate('/settings'),
        keywords: 'config configuration tenant',
      },
      {
        id: 'theme-midnight',
        label: 'Switch to Midnight Pro',
        category: 'Theme',
        action: () => applyPalette('midnight'),
        keywords: 'dark blue theme',
      },
      {
        id: 'theme-ocean',
        label: 'Switch to Ocean Calm',
        category: 'Theme',
        action: () => applyPalette('ocean'),
        keywords: 'teal cyan theme',
      },
      {
        id: 'theme-parchment',
        label: 'Switch to Parchment (Default)',
        category: 'Theme',
        action: () => applyPalette('parchment'),
        keywords: 'default light cream theme',
      },
      {
        id: 'action-sync',
        label: 'Sync Data',
        category: 'Actions',
        action: () => window.location.reload(),
        keywords: 'refresh reload sync',
      },
    ],
    [navigate],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return commands;
    const q = query.toLowerCase();
    return commands.filter(
      (cmd) =>
        cmd.label.toLowerCase().includes(q) ||
        cmd.category.toLowerCase().includes(q) ||
        (cmd.keywords && cmd.keywords.toLowerCase().includes(q)),
    );
  }, [commands, query]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
        setQuery('');
        setSelectedIndex(0);
      }
      if (!open) return;
      if (e.key === 'Escape') {
        setOpen(false);
      }
    },
    [open],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered[selectedIndex]) {
      filtered[selectedIndex].action();
      setOpen(false);
    }
  };

  const executeCommand = (cmd: Command) => {
    cmd.action();
    setOpen(false);
  };

  if (!open) return null;

  // Group by category
  const groups: Record<string, Command[]> = {};
  for (const cmd of filtered) {
    if (!groups[cmd.category]) groups[cmd.category] = [];
    groups[cmd.category].push(cmd);
  }

  let globalIdx = -1;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-[15vh] animate-fade-in">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />
      <div className="relative w-full max-w-lg mx-4 glass-card overflow-hidden animate-slide-up">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-line">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-muted flex-shrink-0"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleInputKeyDown}
            placeholder="Type a command..."
            className="flex-1 bg-transparent text-text text-sm outline-none placeholder:text-muted/60"
          />
          <kbd className="text-[10px] text-muted bg-panel-soft px-1.5 py-0.5 rounded border border-line">
            ESC
          </kbd>
        </div>

        <div className="max-h-72 overflow-y-auto py-2">
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted">
              No commands found
            </p>
          ) : (
            Object.entries(groups).map(([category, cmds]) => (
              <div key={category}>
                <p className="px-4 py-1.5 text-[10px] font-semibold text-muted uppercase tracking-widest">
                  {category}
                </p>
                {cmds.map((cmd) => {
                  globalIdx++;
                  const idx = globalIdx;
                  return (
                    <button
                      key={cmd.id}
                      onClick={() => executeCommand(cmd)}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center gap-3 ${
                        idx === selectedIndex
                          ? 'bg-accent/15 text-text'
                          : 'text-muted hover:bg-panel-soft hover:text-text'
                      }`}
                    >
                      <span className="flex-1">{cmd.label}</span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
